import React, { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiZoomIn, FiDownload } from "react-icons/fi";
import "./ImageLightbox.css";

/**
 * ImageLightbox - Full screen image viewer for thread photos
 * 
 * Features:
 * - Full screen overlay with blur background
 * - Close on backdrop click or escape key
 * - Zoom indicator
 * - Optional download button
 * - Smooth animations
 */
const ImageLightbox = ({ 
  isOpen, 
  imageUrl, 
  alt = "Image", 
  onClose,
  showDownload = false,
  caption = null 
}) => {
  // Handle escape key to close
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      onClose();
    }
  }, [onClose]);

  // Add/remove event listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = "hidden";
    }
    
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  // Handle download
  const handleDownload = useCallback(async (e) => {
    e.stopPropagation();
    
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `image-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) { /* ignored */ }
  }, [imageUrl]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="img-lightbox-overlay"
          onClick={handleBackdropClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="img-lightbox-container"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <motion.button
              className="img-lightbox-close"
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Close image viewer"
            >
              <FiX size={24} />
            </motion.button>

            {/* Download Button (optional) */}
            {showDownload && (
              <motion.button
                className="img-lightbox-download"
                onClick={handleDownload}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Download image"
              >
                <FiDownload size={20} />
              </motion.button>
            )}

            {/* Image */}
            <motion.img
              src={imageUrl}
              alt={alt}
              className="img-lightbox-image"
              draggable={false}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            />

            {/* Zoom hint */}
            <div className="img-lightbox-hint">
              <FiZoomIn size={14} />
              <span>Click outside or press ESC to close</span>
            </div>

            {/* Caption (optional) */}
            {caption && (
              <motion.div 
                className="img-lightbox-caption"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {caption}
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageLightbox;
